import { useEffect, useRef, useState } from "react";
import { IconNameEnum } from "@b2bportal/core-themes";
import { Button } from "@b2bportal/core-ui";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { Icon } from "@tenant/ui";
import type { PerkspotUserInfoResponse } from "../api/auth";
import styles from "./SessionModal.module.scss";
import { EarnPointsDialogControlled } from "@/components/PerkDialogs";
import { TripsLink } from "@/components/TripsLink";
import { FAQ_URL } from "@/utils/externalPages";
import { getHomeUrl } from "@/utils/urls";
import { useNumberFormatter } from "@/utils/useNumberFormatter";

interface ISessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginClick: () => void;
  userDetails: PerkspotUserInfoResponse;
}

export const SessionModal = ({
  isOpen,
  onClose,
  onLoginClick,
  userDetails,
}: ISessionModalProps) => {
  const { t } = useI18nContext();
  const { format } = useNumberFormatter();
  const { firstName, email, hardAuth, pointsBalance } = userDetails;

  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isOpen && !isHowItWorksOpen) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          onClose();
        }
      };
      const handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          onClose();
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleEscapeKey);
      };
    }
  }, [isOpen, isHowItWorksOpen, onClose]);
  if (!isOpen) return null;
  return (
    <div className={styles.container} ref={containerRef}>
      <header className={styles.header}>
        <h1 className={styles.title}>
          {t("headerMenu.welcome", { name: firstName })}
        </h1>
        <p className={styles.email}>{email}</p>
        {!hardAuth ? (
          <p className={styles.loginPrompt}>
            <Trans i18nKey="headerMenu.loginPrompt">
              <button className={styles.loginLink} onClick={onLoginClick} />
            </Trans>
          </p>
        ) : null}
        <button className={styles.closeButton} onClick={onClose}>
          <Icon iconName={IconNameEnum.close} />
        </button>
      </header>
      <div className={styles.bar}>
        <div className={styles.pointsSection}>
          {hardAuth
            ? t("auth.points", { points: format(pointsBalance) })
            : t("headerMenu.signInToSeePoints")}
        </div>
        <button
          className={styles.link}
          onClick={() => setIsHowItWorksOpen(true)}
        >
          {t("headerMenu.howItWorks")}
        </button>
      </div>
      <div>
        <TripsLink className={styles.link}>{t("headerMenu.myTrips")}</TripsLink>
        <a
          className={styles.link}
          href={FAQ_URL}
          target="_blank"
          rel="noopener"
          referrerpolicy="no-referrer-when-downgrade"
        >
          {t("headerMenu.faq")}
        </a>
        <a className={styles.link} href={getHomeUrl()}>
          {t("headerMenu.backToHome")}
        </a>
        {!hardAuth ? (
          <Button
            color="secondary"
            size="small"
            className={styles.loginButton}
            onClick={onLoginClick}
          >
            {t("headerMenu.signIn")}
          </Button>
        ) : null}
      </div>
      <EarnPointsDialogControlled
        open={isHowItWorksOpen}
        onClose={() => setIsHowItWorksOpen(false)}
      />
    </div>
  );
};
